import React from "react";
import { renderInlineIcon } from "../lib/icons";
import { sortAlpha } from "../lib/sortUtils";


function sortMenuItems(items=[]) {
  return sortAlpha(items, (item) => item?.label || item?.name || '').map((item) => ({
    ...item,
    children: Array.isArray(item?.children) && item.children.length ? sortMenuItems(item.children) : item?.children,
  }));
}

function findActiveLeaf(items=[]) {
  for (const item of Array.isArray(items) ? items : []) {
    const hasChildren = Array.isArray(item?.children) && item.children.length > 0;
    if (hasChildren) {
      const nested = findActiveLeaf(item.children);
      if (nested) return nested;
    }
    if (!hasChildren && item?.active) return item;
  }
  return null;
}

function MenuEntry({ T, item, level=0, closeMenu, expandedMap, setExpandedMap, onLeafSelect }) {
  const itemKey = item.id || item.label || `level-${level}`;
  const hasChildren = Array.isArray(item?.children) && item.children.length > 0;
  const isExpanded = !!expandedMap[itemKey];
  const isActive = !!item.active || (hasChildren && item.children.some(child => child.active));
  const accent = item.color || T.accent || "#3B82F6";
  const baseStyle = {
    width:"100%",
    display:"flex",
    alignItems:"center",
    justifyContent:"space-between",
    gap:10,
    padding: level===0 ? "10px 12px" : "9px 12px 9px 14px",
    borderRadius:16,
    border:`1px solid ${isActive ? accent : T.border}`,
    background:isActive ? `${accent}18` : T.card2,
    color:isActive ? accent : T.text,
    cursor:"pointer",
    fontSize:13,
    fontWeight:isActive ? 800 : 700,
    textAlign:"left",
  };

  if (hasChildren) {
    return (
      <div style={{display:"grid",gap:6}}>
        <button
          type="button"
          onClick={(e)=>{
            e.preventDefault();
            e.stopPropagation();
            setExpandedMap(prev=>({ ...prev, [itemKey]: !prev[itemKey] }));
          }}
          style={baseStyle}
        >
          <span style={{display:"inline-flex",alignItems:"center",gap:8,minWidth:0}}>
            {item.icon ? renderInlineIcon(item.icon, { size: 16, title: item.label }) : null}
            <span style={{minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{item.label}</span>
          </span>
          <span style={{fontSize:14,opacity:.9,transform:isExpanded?"rotate(90deg)":"rotate(0deg)",transition:"transform .18s ease"}}>›</span>
        </button>
        {isExpanded ? (
          <div style={{display:"grid",gap:6,paddingLeft: level===0 ? 10 : 8}}>
            {item.children.map((child, idx)=>(
              <MenuEntry
                key={child.id || child.label || `${itemKey}-${idx}`}
                T={T}
                item={child}
                level={level+1}
                closeMenu={closeMenu}
                expandedMap={expandedMap}
                setExpandedMap={setExpandedMap}
                onLeafSelect={onLeafSelect}
              />
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={(e)=>{
        e.preventDefault();
        e.stopPropagation();
        onLeafSelect?.(item);
        item.onClick?.();
        closeMenu?.();
      }}
      style={baseStyle}
    >
      <span style={{display:"inline-flex",alignItems:"center",gap:8,minWidth:0}}>
        {item.icon ? renderInlineIcon(item.icon, { size: 16, title: item.label }) : null}
        <span style={{minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{item.label}</span>
      </span>
      {item.badge ? <span style={{marginLeft:8,background:isActive ? "rgba(255,255,255,.22)" : `${accent}22`,color:isActive ? "#fff" : accent,borderRadius:999,padding:"1px 7px",fontSize:11,fontWeight:800}}>{item.badge}</span> : null}
    </button>
  );
}

export default function ModuleHeaderMenu({ T, title = "Menu do módulo", children, items=null, compact=false, onBack=null }) {
  const [moduleBackAvailable, setModuleBackAvailable] = React.useState(false);
  const ROOT_MARKER = React.useMemo(()=>({ __type:"root" }), []);
  const [open, setOpen] = React.useState(false);
  const close = React.useCallback(()=>setOpen(false), []);
  const normalizedItems = React.useMemo(()=>sortMenuItems(Array.isArray(items) ? items : []), [items]);
  const hasNestedItems = React.useMemo(()=>normalizedItems.some(item => Array.isArray(item?.children) && item.children.length > 0), [normalizedItems]);
  const [expandedMap, setExpandedMap] = React.useState({});
  const [navStack, setNavStack] = React.useState([]);
  const [currentLeaf, setCurrentLeaf] = React.useState(null);
  const rootLeaf = React.useMemo(()=>findActiveLeaf(normalizedItems), [normalizedItems]);

  React.useEffect(()=>{
    setNavStack([]);
    setCurrentLeaf(null);
    setModuleBackAvailable(false);
  }, [title]);

  React.useEffect(()=>{
    const next = {};
    normalizedItems.forEach((item)=>{
      const key = item.id || item.label;
      if (item?.children?.some?.(child => child.active)) next[key] = true;
    });
    setExpandedMap(prev=>{
      const merged = { ...next };
      Object.keys(prev || {}).forEach((key)=>{
        if (prev[key]) merged[key] = true;
      });
      return merged;
    });
  }, [normalizedItems]);

  React.useEffect(()=>{
    if (!normalizedItems.length) return undefined;
    const detail = { title, items: normalizedItems, compact, onBack };
    window.dispatchEvent(new CustomEvent("fz:module-header-menu:register", { detail }));
    return ()=>window.dispatchEvent(new CustomEvent("fz:module-header-menu:clear"));
  }, [title, normalizedItems, compact, onBack]);

  React.useEffect(()=>{
    const onToggle = ()=>setOpen(v=>!v);
    const onClose = ()=>setOpen(false);
    const onSetBackAvailable = (event)=>{
      setModuleBackAvailable(!!event?.detail?.available);
    };
    return (()=>{
      window.addEventListener("fz:module-header-menu:toggle", onToggle);
      window.addEventListener("fz:module-header-menu:close", onClose);
      window.addEventListener("fz:module-header-menu:set-back-available", onSetBackAvailable);
      return ()=>{
        window.removeEventListener("fz:module-header-menu:toggle", onToggle);
        window.removeEventListener("fz:module-header-menu:close", onClose);
        window.removeEventListener("fz:module-header-menu:set-back-available", onSetBackAvailable);
      };
    })();
  }, []);

  const handleLeafSelect = React.useCallback((item)=>{
    setNavStack(prev=>[...prev, currentLeaf || ROOT_MARKER]);
    setCurrentLeaf(item || null);
  }, [currentLeaf, ROOT_MARKER]);

  const goToModuleRoot = React.useCallback(()=>{
    try {
      if (typeof onBack === "function") {
        const handled = onBack();
        if (handled) return true;
      }
    } catch (_) {}
    if (rootLeaf?.onClick) {
      rootLeaf.onClick();
      return true;
    }
    let handled = false;
    const backEvent = new CustomEvent("fz:module-header-menu:back-request", {
      detail: {
        handled: false,
        markHandled(){
          this.handled = true;
        },
      },
    });
    window.dispatchEvent(backEvent);
    handled = !!backEvent.detail?.handled;
    return handled;
  }, [onBack, rootLeaf]);

  const handleBack = React.useCallback(()=>{
    close();
    if (!navStack.length) {
      const backToStartEvent = new CustomEvent("fz:app:navigate-back-to-module-start");
      window.dispatchEvent(backToStartEvent);
      if (moduleBackAvailable) return;
      window.dispatchEvent(new CustomEvent("fz:app:navigate-home"));
      return;
    }
    const previous = navStack[navStack.length - 1];
    setNavStack(prev=>prev.slice(0, -1));
    if (previous?.__type === "root") {
      setCurrentLeaf(null);
      if (goToModuleRoot()) return;
      window.dispatchEvent(new CustomEvent("fz:app:navigate-home"));
      return;
    }
    setCurrentLeaf(previous || null);
    try { previous?.onClick?.(); } catch (_) {}
  }, [close, navStack, goToModuleRoot, moduleBackAvailable]);

  const goHome = React.useCallback(()=>{
    close();
    window.dispatchEvent(new CustomEvent("fz:module-header-menu:go-home"));
  }, [close]);

  const canGoBack = navStack.length > 0 || moduleBackAvailable;

  return (
    <>
      {open && <div onClick={close} style={{position:"fixed", inset:0, background:"rgba(2,6,23,.18)", backdropFilter:"blur(2px)", zIndex:84}} />}
      <div style={{position:"relative", zIndex:85}}>
        <div style={{position:"fixed",top:"max(54px, calc(env(safe-area-inset-top) + 42px))",left:"max(8px, env(safe-area-inset-left))",bottom:"calc(94px + env(safe-area-inset-bottom))",width:"min(260px, calc(100vw - 16px))",background:T.card,border:`1px solid ${T.border}`,borderRadius:22,padding:"12px 10px",boxShadow:"0 24px 52px rgba(2,6,23,.22)",zIndex:85,transform:open?"translateX(0)":"translateX(calc(-100% - 18px))",opacity:open?1:0,pointerEvents:open?"auto":"none",transition:"transform .22s ease, opacity .18s ease",overflowY:"auto"}}>
          <div style={{fontSize:11,fontWeight:800,color:T.muted,textTransform:"uppercase",letterSpacing:.8,padding:"2px 8px 8px"}}>{title}</div>
          <div style={{display:"grid",gap:8,padding:"0 4px 4px"}}>
            {normalizedItems.length>0 ? normalizedItems.map((item, idx)=><MenuEntry key={item.id || item.label || idx} T={T} item={item} closeMenu={close} expandedMap={expandedMap} setExpandedMap={setExpandedMap} onLeafSelect={handleLeafSelect} />) : (
              <div onClickCapture={(e)=>{
                const hit = e.target?.closest?.('button,[role="button"],a,label');
                if(hit) setTimeout(()=>setOpen(false), 0);
              }} style={{display:"grid",gap:8}}>{children}</div>
            )}
            <div style={{display:"grid",gridTemplateColumns:canGoBack?"repeat(2,minmax(0,1fr))":"1fr",gap:8,marginTop:4}}>
              <button
                type="button"
                onClick={goHome}
                style={{
                  width:"100%",
                  display:"flex",
                  alignItems:"center",
                  justifyContent:"center",
                  gap:8,
                  padding:"10px 12px",
                  borderRadius:16,
                  border:`1px solid ${T.border}`,
                  background:`${T.accent}10`,
                  color:T.text,
                  cursor:"pointer",
                  fontSize:13,
                  fontWeight:800,
                }}
              >
                ⌂ Início
              </button>
              {canGoBack ? (
                <button
                  type="button"
                  onClick={handleBack}
                  style={{
                    width:"100%",
                    display:"flex",
                    alignItems:"center",
                    justifyContent:"center",
                    gap:8,
                    padding:"10px 12px",
                    borderRadius:16,
                    border:`1px solid ${T.border}`,
                    background:hasNestedItems ? `${T.accent}10` : T.card2,
                    color:T.text,
                    cursor:"pointer",
                    fontSize:13,
                    fontWeight:800,
                  }}
                >
                  ← Voltar
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
